import { apiClient } from '@/api/client';
import type {
  CreateUserRequest,
  PaginatedUsers,
  Role,
  UpdateUserRequest,
  UserResponse,
} from '@/types/shared';

export interface ListUsersParams {
  page?: number;
  pageSize?: number;
  /** `field:ASC` / `field:DESC` — allowed fields per users.service.ts `applySort`: `fullName`,
   * `email`, `role`, `isActive`, `createdAt`, `lastLoginAt`. */
  sort?: string;
  search?: string;
  role?: Role;
  isActive?: boolean;
}

/** GET /users — Admin-only (T70). Soft-deleted users are excluded server-side; there is no
 * "show deleted" toggle on this screen (see UsersPage doc comment for the deactivate-vs-delete
 * model this UI deliberately settles on). */
export async function listUsers(params: ListUsersParams): Promise<PaginatedUsers> {
  const { data } = await apiClient.get<PaginatedUsers>('/users', { params });
  return data;
}

/** GET /users/{id} — Admin-only. */
export async function getUser(id: string): Promise<UserResponse> {
  const { data } = await apiClient.get<UserResponse>(`/users/${encodeURIComponent(id)}`);
  return data;
}

/** POST /users — Admin-only. Mirrors `CreateUserDto` (backend, frozen): Admin sets the initial
 * password directly (D-U2 — no email/invite pipeline). */
export async function createUser(payload: CreateUserRequest): Promise<UserResponse> {
  const { data } = await apiClient.post<UserResponse>('/users', payload);
  return data;
}

/** PATCH /users/{id} — Admin-only. `email` is deliberately absent from `UpdateUserRequest`
 * (create-only/immutable, D-U3) — never send it here. */
export async function updateUser(id: string, payload: UpdateUserRequest): Promise<UserResponse> {
  const { data } = await apiClient.patch<UserResponse>(`/users/${encodeURIComponent(id)}`, payload);
  return data;
}

/** DELETE /users/{id} — Admin-only soft-delete. Not used by the standard "deactivate" row action
 * (see UsersPage doc comment) — kept here only in case a future deleted-users view needs it,
 * paired with `restoreUser` below. */
export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/users/${encodeURIComponent(id)}`);
}

/** POST /users/{id}/restore — Admin-only. Restores a soft-deleted user (see `deleteUser`). */
export async function restoreUser(id: string): Promise<UserResponse> {
  const { data } = await apiClient.post<UserResponse>(`/users/${encodeURIComponent(id)}/restore`);
  return data;
}

/** Mirrors `SetPasswordDto` (backend). `currentPassword` is only required when the caller is
 * changing their **own** password; omitted entirely on the Admin-resets-another-user path used by
 * this screen's "Reset password" row action. */
export interface SetPasswordPayload {
  newPassword: string;
  currentPassword?: string;
}

/** PATCH /users/{id}/password — no `@Roles()` guard server-side (any authenticated user may call
 * it for themselves; only an Admin may target someone else — enforced in `UsersService.setPassword`). */
export async function setUserPassword(id: string, payload: SetPasswordPayload): Promise<void> {
  await apiClient.patch(`/users/${encodeURIComponent(id)}/password`, payload);
}

/** GET /roles — Admin-only. The static 7-role enum (D-U4: no roles table/CRUD), used to populate
 * the role picker on the create/edit forms and the list's role filter. */
export async function getRoles(): Promise<Role[]> {
  const { data } = await apiClient.get<Role[]>('/roles');
  return data;
}
